import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getForms, duplicateForm } from '../../services/api/formApi';
import CreateFormModal from '../../components/forms/CreateFormModal';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import DashboardHeader from '../../components/layout/DashboardHeader';
import FormActionMenu from '../../components/forms/FormActionMenu';

/**
 * Dashboard page showing user's forms
 */
const Dashboard = () => {
  useDocumentTitle('Dashboard');
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [size] = useState(5); // Standard page size
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Close Form Modal State
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [formToClose, setFormToClose] = useState(null);
  const [closing, setClosing] = useState(false);

  // Schedule Closure Modal State
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [formToSchedule, setFormToSchedule] = useState(null);
  const [scheduledTime, setScheduledTime] = useState('');
  const [scheduling, setScheduling] = useState(false);

  // Duplicate Form Modal State
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [formToDuplicate, setFormToDuplicate] = useState(null);
  const [duplicating, setDuplicating] = useState(false);

  // Toast / Notification State
  const [toastMessage, setToastMessage] = useState(null);

  const navigate = useNavigate();

  const loadForms = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getForms(page, size);
      setForms(data.content || []);
      setTotalPages(data.totalPages || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, size]);

  useEffect(() => {
    loadForms();
  }, [loadForms]); // Reload when page changes

  // Clear toast after 3 seconds
  useEffect(() => {
    if (toastMessage) {
      const timer = setTimeout(() => setToastMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toastMessage]);

  // Handle click outside profile dropdown




  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCreateForm = () => {
    setShowCreateModal(true);
  };

  const handleEditForm = (formId) => {
    navigate(`/form/${formId}`);
  };

  const handleCopyLink = (formId) => {
    const url = `${window.location.origin}/form/${formId}/fill`;
    navigator.clipboard.writeText(url).then(() => {
      setToastMessage('Link copied to clipboard!');
    });
  };

  const handleShare = async (formId) => {
    const url = `${window.location.origin}/form/${formId}/fill`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Check out this form',
          url: url
        });
      } catch (err) {
        // Fallback to copy
        handleCopyLink(formId);
      }
    } else {
      handleCopyLink(formId);
    }
  };

  const initiateCloseForm = (form) => {
    setFormToClose(form);
    setShowCloseModal(true);
  };

  const confirmCloseForm = async () => {
    if (!formToClose) return;

    setClosing(true);
    try {
      // Import closeForm lazily or assume it's imported at top (updating imports below)
      const { closeForm } = await import('../../services/api/formApi');
      await closeForm(formToClose.id);

      // Refresh forms list to show updated badges
      await loadForms();

      setToastMessage('Form closed successfully.');
      setShowCloseModal(false);
      setFormToClose(null);
    } catch (err) {
      console.error('Failed to close form:', err);
      setToastMessage(err.message || 'Failed to close form. Please try again.');
    } finally {
      setClosing(false);
    }
  };

  const initiateScheduleClosure = (form) => {
    setFormToSchedule(form);
    if (form.scheduledCloseAt) {
      // Convert UTC to local datetime-local format: YYYY-MM-DDThh:mm
      const date = new Date(form.scheduledCloseAt);
      // Get local ISO string
      const offset = date.getTimezoneOffset() * 60000;
      const localISOTime = new Date(date.getTime() - offset).toISOString().slice(0, 16);
      setScheduledTime(localISOTime);
    } else {
      setScheduledTime('');
    }
    setShowScheduleModal(true);
  };

  const handleScheduleSubmit = async () => {
    if (!formToSchedule) return;

    // Basic validation: must select a time if setting one
    if (!scheduledTime) {
      setToastMessage("Please select a time.");
      return;
    }

    setScheduling(true);
    try {
      const { scheduleFormClosure } = await import('../../services/api/formApi');

      const date = new Date(scheduledTime);
      const timeToSend = date.toISOString(); // Converts to UTC

      await scheduleFormClosure(formToSchedule.id, timeToSend);
      await loadForms();
      setToastMessage('Form closure scheduled successfully.');
      setShowScheduleModal(false);
      setFormToSchedule(null);
    } catch (err) {
      console.error('Failed to schedule closure:', err);
      setToastMessage(err.message || 'Failed to schedule closure.');
    } finally {
      setScheduling(false);
    }
  };

  const handleRemoveSchedule = async () => {
    if (!formToSchedule) return;

    setScheduling(true);
    try {
      const { scheduleFormClosure } = await import('../../services/api/formApi');
      await scheduleFormClosure(formToSchedule.id, null);

      await loadForms();
      setToastMessage('Schedule removed successfully.');
      setShowScheduleModal(false);
      setFormToSchedule(null);
    } catch (err) {
      console.error('Failed to remove schedule:', err);
      setToastMessage(err.message || 'Failed to remove schedule.');
    } finally {
      setScheduling(false);
    }
  };

  const initiateDuplicateForm = (form) => {
    setFormToDuplicate(form);
    setShowDuplicateModal(true);
  };

  const confirmDuplicateForm = async () => {
    if (!formToDuplicate) return;

    setDuplicating(true);
    try {
      const duplicatedForm = await duplicateForm(formToDuplicate.id);

      // Refresh the forms list
      await loadForms();

      setToastMessage(`"${formToDuplicate.title}" duplicated successfully!`);
      setShowDuplicateModal(false);
      setFormToDuplicate(null);

      // Navigate to edit the new form after a short delay
      setTimeout(() => {
        navigate(`/form/${duplicatedForm.id}`);
      }, 1500);
    } catch (err) {
      console.error('Failed to duplicate form:', err);
      setToastMessage(err.message || 'Failed to duplicate form.');
    } finally {
      setDuplicating(false);
    }
  };




  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading your forms...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ background: '#f8fafc' }}>
      {/* Toast Notification */}
      {toastMessage && (
        <div className="position-fixed bottom-0 end-0 p-3" style={{ zIndex: 1100 }}>
          <div className="toast show align-items-center text-white bg-dark border-0" role="alert" aria-live="assertive" aria-atomic="true">
            <div className="d-flex">
              <div className="toast-body">
                {toastMessage}
              </div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToastMessage(null)}></button>
            </div>
          </div>
        </div>
      )}

      <DashboardHeader />

      <main className="container py-4 pb-5">
        <div className="dashboard-header-section">
          <div className="text-center text-md-start">
            <h2 className="display-6 fw-bold mb-2">Dashboard</h2>
            <p className="text-muted mb-0">Manage your forms and responses</p>
          </div>
          <div className="w-100 w-md-auto d-flex justify-content-center justify-content-md-end">
            <button
              onClick={handleCreateForm}
              className="btn-premium-action"
            >
              <i className="fa fa-plus me-2" aria-hidden="true"></i>
              <span>CREATE NEW FORM</span>
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger mb-4 shadow-sm border-0">{error}</div>}

        <div>
          {forms.length === 0 ? (
            <div className="text-center py-5 bg-white rounded-4 shadow-sm border border-light">
              <div className="mb-4 text-muted opacity-25">
                <i className="fa fa-folder-open-o" aria-hidden="true" style={{ fontSize: '64px' }}></i>
              </div>
              <h3 className="h5 mb-2">No forms yet</h3>
              <p className="text-muted mb-4">Create your first form to get started</p>
              <button onClick={handleCreateForm} className="btn-primary">
                Create Form
              </button>
            </div>
          ) : (
            <div className="row g-4">
              {forms.map((form) => {
                const isPublished = form.status === 'PUBLISHED';
                return (
                  <div key={form.id} className="col-lg-4 col-md-6">
                    <div className="card h-100 border-0 shadow-sm hover-shadow transition-all" style={{ borderRadius: '16px' }}>
                      <div className="card-body p-4">
                        <div className="d-flex justify-content-between align-items-start mb-3">
                          <span className={`badge rounded-pill px-3 py-2 ${form.status?.toLowerCase() === 'published' ? 'bg-success-subtle text-success' :
                            form.status?.toLowerCase() === 'draft' ? 'bg-warning-subtle text-warning-emphasis' :
                              'bg-secondary-subtle text-secondary'
                            }`}>
                            {form.status}
                          </span>
                        </div>

                        <h5 className="card-title fw-bold mb-2 text-truncate">{form.title || 'Untitled Form'}</h5>
                        <p className="card-text text-muted mb-4 text-truncate" style={{ height: '24px' }}>
                          {form.description || 'No description provided'}
                        </p>

                        {form.scheduledCloseAt && (
                          <div className="mb-3 d-flex align-items-center text-warning-emphasis bg-warning-subtle px-2 py-1 rounded small" style={{ width: 'fit-content' }}>
                            <i className="fa fa-clock-o me-2" aria-hidden="true"></i>
                            <span className="fw-medium">Closes: {new Date(form.scheduledCloseAt).toLocaleString()}</span>
                          </div>
                        )}

                        <div className="d-flex align-items-center text-secondary small mb-4">
                          <i className="fa fa-calendar me-2" aria-hidden="true"></i>
                          {form.createdAt ? new Date(form.createdAt).toLocaleDateString() : 'N/A'}
                          <span className="mx-2">•</span>
                          <span>{form.questionCount || 0} questions</span>
                          {form.status !== 'DRAFT' && (
                            <>
                              <span className="mx-2">•</span>
                              <span>{form.responseCount || 0} responses</span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="card-footer bg-white border-top border-light p-3 d-flex align-items-center justify-content-end gap-2" style={{ borderRadius: '0 0 16px 16px' }}>
                        {/* Primary Actions based on Status */}

                        {/* View Responses - Visible for Published/Closed */}
                        {(isPublished || form.status === 'CLOSED') && (
                          <button
                            onClick={() => window.open(`/form/${form.id}/responses`, '_blank')}
                            className="btn btn-light text-primary fw-medium p-2 rounded-circle d-flex align-items-center justify-content-center me-auto"
                            title="View Responses"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <i className="fa fa-list-alt" aria-hidden="true"></i>
                          </button>
                        )}

                        {/* Edit - Visible for Draft */}
                        {form.status === 'DRAFT' && (
                          <button
                            onClick={() => handleEditForm(form.id)}
                            className="btn btn-light text-primary fw-medium p-2 rounded-circle d-flex align-items-center justify-content-center me-auto"
                            title="Edit Form"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <i className="fa fa-pencil" aria-hidden="true"></i>
                          </button>
                        )}

                        {/* If neither (shouldn't happen with current logic, but for safety keep me-auto spacer if needed) */}
                        {!isPublished && form.status !== 'CLOSED' && form.status !== 'DRAFT' && <div className="me-auto"></div>}

                        {/* Copy Link - Visible only for Published */}
                        {isPublished && (
                          <button
                            onClick={() => handleCopyLink(form.id)}
                            className="btn btn-light text-primary fw-medium p-2 rounded-circle d-flex align-items-center justify-content-center"
                            title="Copy Link"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <i className="fa fa-link" aria-hidden="true"></i>
                          </button>
                        )}

                        {/* Action Menu - Contains everything else */}
                        <FormActionMenu
                          onDuplicate={() => initiateDuplicateForm(form)}
                          onEdit={() => handleEditForm(form.id)}
                          onShare={() => handleShare(form.id)}
                          onSchedule={() => initiateScheduleClosure(form)}
                          onClose={() => initiateCloseForm(form)}
                          status={form.status}
                          scheduledCloseAt={form.scheduledCloseAt}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Pagination Controls */}
        {
          !loading && !error && forms.length > 0 && totalPages > 1 && (
            <div className="d-flex justify-content-center align-items-center mt-5 gap-3">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 0}
                className="btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm"
                style={{ width: '40px', height: '40px' }}
              >
                <i className="fa fa-chevron-left" aria-hidden="true"></i>
              </button>

              <span className="text-muted fw-medium small">
                Page {page + 1} of {totalPages}
              </span>

              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === totalPages - 1}
                className="btn btn-light rounded-circle p-2 d-flex align-items-center justify-content-center shadow-sm"
                style={{ width: '40px', height: '40px' }}
              >
                <i className="fa fa-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          )
        }
      </main >

      <CreateFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
      />

      {
        showCloseModal && formToClose && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg p-4 rounded-4">
                <div className="text-center mb-3">
                  <div className="bg-danger-subtle text-danger p-3 rounded-circle d-inline-flex mb-3">
                    <i className="fa fa-exclamation-triangle fa-2x" aria-hidden="true"></i>
                  </div>
                  <h4 className="fw-bold">Close Form?</h4>
                  <p className="text-secondary">
                    Are you sure you want to close <strong>"{formToClose.title}"</strong>?<br />
                    This will stop accepting new responses immediately.
                  </p>
                </div>
                <div className="d-flex gap-3 justify-content-center">
                  <button
                    onClick={() => setShowCloseModal(false)}
                    className="btn btn-light px-4 fw-medium"
                    disabled={closing}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmCloseForm}
                    className="btn btn-danger px-4 fw-medium shadow-sm"
                    disabled={closing}
                  >
                    {closing ? 'Closing...' : 'Yes, Close Form'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Schedule Closure Modal */}
      {
        showScheduleModal && formToSchedule && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg p-4 rounded-4">
                <div className="text-center mb-3">
                  <div className={`${formToSchedule.scheduledCloseAt ? 'bg-warning-subtle text-warning-emphasis' : 'bg-primary-subtle text-primary'} p-3 rounded-circle d-inline-flex mb-3`}>
                    <i className="fa fa-clock-o fa-2x" aria-hidden="true"></i>
                  </div>
                  <h4 className="fw-bold">Schedule Closure</h4>
                  <p className="text-secondary">
                    Automatically close <strong>"{formToSchedule.title}"</strong> at a future date and time.
                  </p>
                  <div className="mb-3 text-start">
                    <label htmlFor="scheduleTime" className="form-label fw-bold small text-secondary">CLOSE AT (LOCAL TIME)</label>
                    <input
                      type="datetime-local"
                      id="scheduleTime"
                      className="form-control form-control-lg"
                      value={scheduledTime}
                      onChange={(e) => setScheduledTime(e.target.value)}
                      // Set min to current time (approx)
                      min={new Date(new Date().getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)}
                    />
                    <div className="form-text">
                      {scheduledTime ? `Will close on ${new Date(scheduledTime).toLocaleString()}` : 'Select a date and time'}
                    </div>
                  </div>
                </div>
                <div className="d-flex gap-3 justify-content-center">
                  <button
                    onClick={() => setShowScheduleModal(false)}
                    className="btn btn-light px-4 fw-medium"
                    disabled={scheduling}
                  >
                    Cancel
                  </button>
                  {formToSchedule.scheduledCloseAt && (
                    <button
                      onClick={handleRemoveSchedule}
                      className="btn btn-outline-danger px-4 fw-medium"
                      disabled={scheduling}
                    >
                      {scheduling ? 'Updating...' : 'Clear Schedule'}
                    </button>
                  )}
                  <button
                    onClick={handleScheduleSubmit}
                    className="btn btn-primary px-4 fw-medium shadow-sm"
                    disabled={scheduling}
                  >
                    {scheduling ? 'Saving...' : 'Save Schedule'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Duplicate Form Modal */}
      {
        showDuplicateModal && formToDuplicate && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content border-0 shadow-lg p-4 rounded-4">
                <div className="text-center mb-3">
                  <div className="bg-success-subtle text-success p-3 rounded-circle d-inline-flex mb-3">
                    <i className="fa fa-clone fa-2x" aria-hidden="true"></i>
                  </div>
                  <h4 className="fw-bold">Duplicate Form?</h4>
                  <p className="text-secondary">
                    Create a copy of <strong>"{formToDuplicate.title}"</strong>?<br />
                    All questions and settings will be duplicated.
                  </p>
                  <div className="text-start bg-light p-3 rounded mt-3">
                    <small className="text-secondary">
                      <strong>Note:</strong> The duplicate will be created as a <span className="badge bg-warning-subtle text-warning-emphasis">DRAFT</span>
                      {formToDuplicate.status !== 'DRAFT' && <span> and responses will not be copied</span>}.
                    </small>
                  </div>
                </div>
                <div className="d-flex gap-3 justify-content-center">
                  <button
                    onClick={() => {
                      setShowDuplicateModal(false);
                      setFormToDuplicate(null);
                    }}
                    className="btn btn-light px-4 fw-medium"
                    disabled={duplicating}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDuplicateForm}
                    className="btn btn-success px-4 fw-medium shadow-sm"
                    disabled={duplicating}
                  >
                    {duplicating ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Duplicating...
                      </>
                    ) : (
                      'Yes, Duplicate'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default Dashboard;
